import {zodResolver} from '@hookform/resolvers/zod';
import {Modal, TextInput} from '@mantine/core';
import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import React, {useEffect} from 'react';
import {Controller, useForm} from 'react-hook-form';
import type {FormCategorySimpleResponse} from 'src/api/ProactApiClient';
import {DialogButton} from 'src/design/molecules/DialogButton';
import {SubmitButton} from 'src/design/molecules/SubmitButton';
import {
  useCreateFormCategoryMutation,
  useUpdateFormCategoryMutation,
} from 'src/redux/queries/formCategory';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {z} from 'zod';

const NAME_MAX_LENGTH = 100;

const schema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Name is required.')
    .max(NAME_MAX_LENGTH, `Must be ${NAME_MAX_LENGTH} characters or fewer.`),
});

type FormCategoryFormValues = z.infer<typeof schema>;

export interface FormCategoryModalProps {
  opened: boolean;
  /** Category to rename; omit to create a new one. */
  category?: FormCategorySimpleResponse;
  onClose: () => void;
}

export const FormCategoryModal: React.FC<FormCategoryModalProps> = ({
  opened,
  category,
  onClose,
}) => {
  const {setAlert} = useAlert();
  const [createFormCategory, {isLoading: isCreating}] =
    useCreateFormCategoryMutation();
  const [updateFormCategory, {isLoading: isUpdating}] =
    useUpdateFormCategoryMutation();

  const {control, handleSubmit, reset} = useForm<FormCategoryFormValues>({
    mode: 'onSubmit',
    defaultValues: {name: ''},
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (opened) {
      reset({name: category?.name ?? ''});
    }
  }, [opened, category, reset]);

  const onSubmit = async (values: FormCategoryFormValues) => {
    try {
      if (category) {
        await updateFormCategory({id: category.id, name: values.name}).unwrap();
        setAlert(`${values.name} was successfully updated.`, 'success');
      } else {
        await createFormCategory({name: values.name}).unwrap();
        setAlert(`${values.name} was successfully created.`, 'success');
      }
      onClose();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={category ? 'Edit Category' : 'New Category'}
      size="md"
      styles={{title: {fontWeight: 700}}}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap="md">
          <Controller
            name="name"
            control={control}
            render={({field, fieldState}) => (
              <TextInput
                label="Name"
                placeholder="e.g. Vehicle Inspections"
                required
                maxLength={NAME_MAX_LENGTH}
                data-autofocus
                {...field}
                error={fieldState.error?.message}
              />
            )}
          />
          <Divider />
          <Group justify="flex-start">
            <DialogButton size="sm" onClick={onClose}>
              Cancel
            </DialogButton>
            <SubmitButton
              size="sm"
              color="yellow"
              loading={isCreating || isUpdating}>
              Save
            </SubmitButton>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};
